/**
 * The one error shape of the app.
 *
 * Every hg-* service answers a failure with RFC 9457 problem details
 * (`application/problem+json`): `type`, `title`, `status`, a stable `code` to
 * branch on, the `request_id` that is in every log line of the request, and
 * `errors` for the fields of a `validation_failed`. Some codes add members of
 * their own (`login_ticket`, `change_ticket`, `reason`, ...).
 *
 * Branch on `code`, never on `status` or on the text. Show `title`: the
 * service already wrote it in the language of `Accept-Language`.
 */

export interface ProblemField {
  /** The field's name in the API. */
  field: string;
  /** The rule it broke. */
  code: string;
  /** For people, in the request's language. */
  message: string;
}

interface ProblemInit {
  status: number;
  code: string;
  title: string;
  type?: string;
  detail?: string | null;
  requestId?: string | null;
  errors?: readonly ProblemField[];
  members?: Readonly<Record<string, unknown>>;
  retryAfter?: number | null;
}

/**
 * Codes this app makes up for failures no service described. Everything else
 * in `ProblemError.code` came from a service.
 */
export const CLIENT_CODES = {
  /** No answer at all: offline, DNS, the service is down, the request was cut. */
  network: "network_error",
  /** An answer that is not problem+json: a gateway's HTML page, an empty 502. */
  unexpected: "unexpected_response",
} as const;

export class ProblemError extends Error {
  override readonly name = "ProblemError";
  /** The HTTP status; 0 when there was no answer. */
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly type: string;
  readonly detail: string | null;
  /** The service's id for the request; null when no service answered. */
  readonly requestId: string | null;
  readonly errors: readonly ProblemField[];
  /** Whatever else the body carried. */
  readonly members: Readonly<Record<string, unknown>>;
  /** Seconds to wait, from `Retry-After`. */
  readonly retryAfter: number | null;

  constructor(init: ProblemInit, options?: ErrorOptions) {
    super(`${init.code}: ${init.title}`, options);
    this.status = init.status;
    this.code = init.code;
    this.title = init.title;
    this.type = init.type ?? "about:blank";
    this.detail = init.detail ?? null;
    this.requestId = init.requestId ?? null;
    this.errors = init.errors ?? [];
    this.members = init.members ?? {};
    this.retryAfter = init.retryAfter ?? null;
  }

  /** A member some codes add, when it is a string (`login_ticket`, `reason`). */
  text(member: string): string | null {
    const value = this.members[member];
    return typeof value === "string" ? value : null;
  }
}

export function isProblem(value: unknown): value is ProblemError {
  return value instanceof ProblemError;
}

const KNOWN = new Set(["type", "title", "status", "code", "detail", "request_id", "errors"]);

/** Whether a failure carries this `code`. */
export function hasCode(error: unknown, code: string): boolean {
  return isProblem(error) && error.code === code;
}

/** The message of the first field problem among `fields`, when the failure names one. */
export function fieldMessage(error: unknown, fields: readonly string[]): string | undefined {
  if (!isProblem(error)) return undefined;
  return error.errors.find(({ field }) => fields.includes(field))?.message;
}

/** Reads a failed response. Never throws: an unreadable body is a problem too. */
export async function problemFromResponse(response: Response): Promise<ProblemError> {
  const retryAfter = seconds(response.headers.get("Retry-After"));
  const body = await readJson(response);

  if (!isRecord(body) || typeof body["code"] !== "string") {
    return new ProblemError({
      status: response.status,
      code: CLIENT_CODES.unexpected,
      title: `HTTP ${String(response.status)}`,
      requestId: response.headers.get("X-Request-Id"),
      retryAfter,
    });
  }

  const members: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(body)) {
    if (!KNOWN.has(key)) members[key] = value;
  }

  return new ProblemError({
    status: response.status,
    code: body["code"],
    title: text(body["title"]) ?? `HTTP ${String(response.status)}`,
    type: text(body["type"]) ?? "about:blank",
    detail: text(body["detail"]),
    requestId: text(body["request_id"]) ?? response.headers.get("X-Request-Id"),
    errors: Array.isArray(body["errors"]) ? body["errors"].filter(isProblemField) : [],
    members,
    retryAfter,
  });
}

/** `fetch` rejected: nothing came back. */
export function networkProblem(cause: unknown): ProblemError {
  return new ProblemError(
    { status: 0, code: CLIENT_CODES.network, title: "No answer from the server" },
    { cause },
  );
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return (await response.clone().json()) as unknown;
  } catch {
    return undefined;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isProblemField(value: unknown): value is ProblemField {
  return (
    isRecord(value) &&
    typeof value["field"] === "string" &&
    typeof value["code"] === "string" &&
    typeof value["message"] === "string"
  );
}

function text(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function seconds(header: string | null): number | null {
  if (header === null) return null;
  const value = Number(header);
  return Number.isFinite(value) && value >= 0 ? value : null;
}
